import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import { UserSearchInput } from "@/components/admin/UserSearchInput";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function DailyVipStreaks() {
  const toast = useToast();
  const [uid, setUid] = useState("");
  const [streak, setStreak] = useState(1);
  const [type, setType] = useState("daily");
  const [server, setServer] = useState("");
  const [servers, setServers] = useState([]);
  const [current, setCurrent] = useState(null);
  useEffect(() => {
    api.get("/live/servers").then(({ data }) => {
      const list = data.servers || [];
      setServers(list);
      if (list.length > 0) setServer(list[0].name || list[0].slug);
    }).catch(() => {});
  }, []);
  const check = async () => {
    if (!uid) return;
    try { const { data } = await api.get(`/admin/${type}/streak/${encodeURIComponent(uid)}`); setCurrent(data); }
    catch { setCurrent(null); }
  };
  const setStreakNow = async () => {
    if (!uid) return toast.error("User ID required");
    try {
      await api.post(`/admin/${type}/streak/set`, { user_id: uid, streak: parseInt(streak, 10) || 0 });
      toast.success(`${type} streak set to ${streak}`);
      check();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reset = async () => {
    if (!uid) return toast.error("User ID required");
    if (!window.confirm(`Reset ${type} streak for ${uid} to 1?`)) return;
    try { await api.post(`/admin/${type}/streak/reset`, { user_id: uid }); toast.success("Reset to 1"); check(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetCooldown = async () => {
    if (!uid || !server) return toast.error("User ID + server required");
    try {
      const { data } = await api.post("/admin/daily/cooldown/reset", { user_id: uid, server_name: server, command: "daily" });
      toast.success(`Cooldown reset (${data.rows_deleted} row${data.rows_deleted === 1 ? "" : "s"})`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4 fade-up" data-testid="streaks-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Daily / VIP Streak Admin</h3>
        <p className="text-xs text-[var(--muted)]">
          Set, reset, or check daily/VIP streaks. Reset cooldown lets a user run <code>/daily</code> again immediately on a specific server.
        </p>
      </div>
      <div className="glass-card p-5 space-y-3">
        <div className="flex gap-2">
          {["daily", "vip"].map((t) => (
            <button key={t} onClick={() => setType(t)} className={`btn btn-sm ${type === t ? "" : "btn-secondary"}`} data-testid={`streak-type-${t}`}>{t}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="User (Discord name or ID)">
            <UserSearchInput
              value={uid}
              onChange={setUid}
              onPick={(row) => setUid(row.discord_id)}
              scope="discord_id"
              placeholder="Start typing — picks a Discord ID"
              dataTestid="streak-user-id"
            />
          </Field>
          <Field label="Streak value"><input className="input" type="number" min="0" max="365" value={streak} onChange={(e) => setStreak(e.target.value)} data-testid="streak-value" /></Field>
          <Field label="Server (for cooldown reset)">
            <select className="input" value={server} onChange={(e) => setServer(e.target.value)} data-testid="streak-server">
              {servers.map((s) => <option key={s.slug} value={s.name || s.slug}>{s.name || s.slug}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={setStreakNow} className="btn" data-testid="streak-set">Set {type} streak</button>
          <button onClick={reset} className="btn btn-secondary" data-testid="streak-reset">Reset to 1</button>
          <button onClick={resetCooldown} className="btn btn-secondary" data-testid="cooldown-reset" disabled={type !== "daily"}>Reset daily cooldown</button>
          <button onClick={check} className="btn btn-secondary" data-testid="streak-check">Check current</button>
        </div>
        {current && (
          <div className="text-sm bg-black/30 rounded p-3 mt-2" data-testid="streak-current">
            <span className="font-mono">{current.user_id}</span>: streak = <strong>{current.streak}</strong>{" "}
            {current.last_claim && <span className="text-[var(--muted)]">· last claim {current.last_claim}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
